import { Button, Stack, Switch, Text, TextInput } from '@mantine/core';
import ResponsiveRow from '@/shared/responsive/ResponsiveRow';
import type { ProviderType } from '@bmai/shared';
import IntegrationValidationDetails from '../IntegrationValidationDetails';
import type { IntegrationValidationState } from '../IntegrationsPageViewModel';
import { resolveDisplayNameTemplate } from './resolveDisplayNameTemplate';

interface Props {
    providerType: ProviderType;
    config: Record<string, unknown>;
    displayName: string;
    enabled: boolean;
    canValidate: boolean;
    validation: IntegrationValidationState;
    onDisplayNameChange: (value: string) => void;
    onEnabledChange: (value: boolean) => void;
    onValidate: () => void;
}

export default function ConnectionStepVerify({
    providerType,
    config,
    displayName,
    enabled,
    canValidate,
    validation,
    onDisplayNameChange,
    onEnabledChange,
    onValidate,
}: Props): React.ReactElement {
    const suggested = providerType.displayNameTemplate
        ? resolveDisplayNameTemplate(providerType.displayNameTemplate, config)
        : null;
    const placeholder = suggested ?? providerType.displayName;
    const nameDescription = suggested
        ? `Leave blank to use "${suggested}".`
        : "Lets you tell apart multiple connections of the same type. Leave blank to use the provider name.";

    return (
        <Stack>
            <TextInput
                label="Name this connection (optional)"
                description={nameDescription}
                placeholder={placeholder}
                value={displayName}
                onChange={(e) => onDisplayNameChange(e.currentTarget.value)}
                maxLength={80}
            />
            <Switch
                label="Enabled"
                description="Disabled connections are retained but skipped during sync"
                checked={enabled}
                onChange={(e) => onEnabledChange(e.currentTarget.checked)}
            />
            {canValidate ? (
                <Stack gap="xs">
                    <Text size="sm" c="dimmed">
                        Test the connection before saving to confirm the credentials and configuration work.
                    </Text>
                    <ResponsiveRow justify="flex-start" align={{ base: 'stretch', sm: 'flex-start' }} wrap="nowrap">
                        <Button variant="default" onClick={onValidate} loading={validation.running}>
                            Test connection
                        </Button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <IntegrationValidationDetails validation={validation} />
                        </div>
                    </ResponsiveRow>
                </Stack>
            ) : (
                <Text size="sm" c="dimmed">
                    Testing isn't available until sign-in completes.
                </Text>
            )}
        </Stack>
    );
}
